/**
 * Single source of truth for status / priority / judgment colours and labels.
 *
 * - Tailwind: every class string below is a literal so the content scanner keeps it.
 * - recharts / TipTap: use the hex values (`hex` for light, `hexDark` for dark).
 *
 * Red is reserved for "needs attention" (overdue, errors, Short coverage). Statuses that
 * merely mean "in flight" use blue so tasks and projects read the same way.
 */

import type { Project, Task } from '@shared/types';
import type { CoverageJudgment } from '@shared/kpiCoverage';

export type Tone =
  | 'gray'
  | 'sky'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'orange'
  | 'red'
  | 'green'
  | 'emerald'
  | 'yellow'
  | 'indigo'
  | 'teal';

export interface ToneStyle {
  /** Tailwind 500 shade - charts and dots in light mode */
  hex: string;
  /** Tailwind 400 shade - charts in dark mode */
  hexDark: string;
  /** Tinted badge */
  soft: string;
  /** Filled badge / button */
  solid: string;
  /** Bordered badge */
  outline: string;
  /** Small status dot */
  dot: string;
  /** Coloured text */
  text: string;
  /** Bar / progress segment */
  bar: string;
  /** Border in the tone colour (kanban card edge, selected filter card) */
  border: string;
  /** Very light surface (kanban column, grouped-list header) */
  tint: string;
}

export const TONES: Record<Tone, ToneStyle> = {
  gray: {
    hex: '#6b7280',
    hexDark: '#9ca3af',
    soft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    solid: 'bg-gray-600 text-white',
    outline: 'border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300',
    dot: 'bg-gray-400',
    text: 'text-gray-600 dark:text-gray-400',
    bar: 'bg-gray-400',
    border: 'border-gray-500',
    tint: 'bg-gray-50 dark:bg-gray-900/20',
  },
  sky: {
    hex: '#0ea5e9',
    hexDark: '#38bdf8',
    soft: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
    solid: 'bg-sky-600 text-white',
    outline: 'border border-sky-300 text-sky-700 dark:border-sky-700 dark:text-sky-300',
    dot: 'bg-sky-500',
    text: 'text-sky-600 dark:text-sky-400',
    bar: 'bg-sky-500',
    border: 'border-sky-500',
    tint: 'bg-sky-50 dark:bg-sky-900/20',
  },
  blue: {
    hex: '#3b82f6',
    hexDark: '#60a5fa',
    soft: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    solid: 'bg-blue-600 text-white',
    outline: 'border border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300',
    dot: 'bg-blue-500',
    text: 'text-blue-600 dark:text-blue-400',
    bar: 'bg-blue-500',
    border: 'border-blue-500',
    tint: 'bg-blue-50 dark:bg-blue-900/20',
  },
  violet: {
    hex: '#8b5cf6',
    hexDark: '#a78bfa',
    soft: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
    solid: 'bg-violet-600 text-white',
    outline: 'border border-violet-300 text-violet-700 dark:border-violet-700 dark:text-violet-300',
    dot: 'bg-violet-500',
    text: 'text-violet-600 dark:text-violet-400',
    bar: 'bg-violet-500',
    border: 'border-violet-500',
    tint: 'bg-violet-50 dark:bg-violet-900/20',
  },
  amber: {
    hex: '#f59e0b',
    hexDark: '#fbbf24',
    soft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    solid: 'bg-amber-600 text-white',
    outline: 'border border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
    text: 'text-amber-600 dark:text-amber-400',
    bar: 'bg-amber-500',
    border: 'border-amber-500',
    tint: 'bg-amber-50 dark:bg-amber-900/20',
  },
  orange: {
    hex: '#f97316',
    hexDark: '#fb923c',
    soft: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    solid: 'bg-orange-600 text-white',
    outline: 'border border-orange-300 text-orange-700 dark:border-orange-700 dark:text-orange-300',
    dot: 'bg-orange-500',
    text: 'text-orange-600 dark:text-orange-400',
    bar: 'bg-orange-500',
    border: 'border-orange-500',
    tint: 'bg-orange-50 dark:bg-orange-900/20',
  },
  red: {
    hex: '#ef4444',
    hexDark: '#f87171',
    soft: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    solid: 'bg-red-600 text-white',
    outline: 'border border-red-300 text-red-700 dark:border-red-700 dark:text-red-300',
    dot: 'bg-red-500',
    text: 'text-red-600 dark:text-red-400',
    bar: 'bg-red-500',
    border: 'border-red-500',
    tint: 'bg-red-50 dark:bg-red-900/20',
  },
  green: {
    hex: '#22c55e',
    hexDark: '#4ade80',
    soft: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    solid: 'bg-green-600 text-white',
    outline: 'border border-green-300 text-green-700 dark:border-green-700 dark:text-green-300',
    dot: 'bg-green-500',
    text: 'text-green-600 dark:text-green-400',
    bar: 'bg-green-500',
    border: 'border-green-500',
    tint: 'bg-green-50 dark:bg-green-900/20',
  },
  emerald: {
    hex: '#10b981',
    hexDark: '#34d399',
    soft: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    solid: 'bg-emerald-600 text-white',
    outline: 'border border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-400',
    bar: 'bg-emerald-500',
    border: 'border-emerald-500',
    tint: 'bg-emerald-50 dark:bg-emerald-900/20',
  },
  yellow: {
    hex: '#eab308',
    hexDark: '#facc15',
    soft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    solid: 'bg-yellow-500 text-white',
    outline: 'border border-yellow-300 text-yellow-700 dark:border-yellow-700 dark:text-yellow-300',
    dot: 'bg-yellow-500',
    text: 'text-yellow-600 dark:text-yellow-400',
    bar: 'bg-yellow-500',
    border: 'border-yellow-500',
    tint: 'bg-yellow-50 dark:bg-yellow-900/20',
  },
  indigo: {
    hex: '#6366f1',
    hexDark: '#818cf8',
    soft: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
    solid: 'bg-indigo-600 text-white',
    outline: 'border border-indigo-300 text-indigo-700 dark:border-indigo-700 dark:text-indigo-300',
    dot: 'bg-indigo-500',
    text: 'text-indigo-600 dark:text-indigo-400',
    bar: 'bg-indigo-500',
    border: 'border-indigo-500',
    tint: 'bg-indigo-50 dark:bg-indigo-900/20',
  },
  teal: {
    hex: '#14b8a6',
    hexDark: '#2dd4bf',
    soft: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
    solid: 'bg-teal-600 text-white',
    outline: 'border border-teal-300 text-teal-700 dark:border-teal-700 dark:text-teal-300',
    dot: 'bg-teal-500',
    text: 'text-teal-600 dark:text-teal-400',
    bar: 'bg-teal-500',
    border: 'border-teal-500',
    tint: 'bg-teal-50 dark:bg-teal-900/20',
  },
};

export interface StatusMeta<V extends string = string> {
  value: V;
  label: string;
  tone: Tone;
  /** Display / sort order within its group */
  order: number;
  /** Short explanation for tooltips */
  hint?: string;
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export type TaskStatus = Task['status'];

export const TASK_STATUS: Record<TaskStatus, StatusMeta<TaskStatus>> = {
  todo: { value: 'todo', label: 'To Do', tone: 'gray', order: 0 },
  open: { value: 'open', label: 'Open', tone: 'sky', order: 1 },
  'in-progress': { value: 'in-progress', label: 'In Progress', tone: 'blue', order: 2 },
  reviewing: { value: 'reviewing', label: 'Reviewing', tone: 'violet', order: 3 },
  pending: { value: 'pending', label: 'Pending', tone: 'amber', order: 4 },
  complete: { value: 'complete', label: 'Complete', tone: 'green', order: 5 },
};

export const TASK_STATUS_ORDER: TaskStatus[] = ['todo', 'open', 'in-progress', 'reviewing', 'pending', 'complete'];

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export type ProjectStatus = Project['status'];

export const PROJECT_STATUS: Record<ProjectStatus, StatusMeta<ProjectStatus>> = {
  'not-started': { value: 'not-started', label: 'Not Started', tone: 'gray', order: 0, hint: 'Planned or just an idea' },
  'in-progress': { value: 'in-progress', label: 'In Progress', tone: 'blue', order: 1, hint: 'Being worked on' },
  completed: { value: 'completed', label: 'Completed', tone: 'green', order: 2, hint: 'Done' },
  'on-hold': { value: 'on-hold', label: 'On Hold', tone: 'amber', order: 3, hint: 'Paused or cancelled; not counted' },
};

export const PROJECT_STATUS_ORDER: ProjectStatus[] = ['not-started', 'in-progress', 'completed', 'on-hold'];

// ---------------------------------------------------------------------------
// Priority (shared by tasks and projects)
// ---------------------------------------------------------------------------

export type Priority = NonNullable<Task['priority']>;

export const PRIORITY: Record<Priority, StatusMeta<Priority>> = {
  urgent: { value: 'urgent', label: 'Urgent', tone: 'red', order: 0 },
  high: { value: 'high', label: 'High', tone: 'orange', order: 1 },
  normal: { value: 'normal', label: 'Normal', tone: 'blue', order: 2 },
  low: { value: 'low', label: 'Low', tone: 'gray', order: 3 },
};

export const PRIORITY_ORDER: Priority[] = ['urgent', 'high', 'normal', 'low'];

/** Used when a task/project has no priority set. */
export const PRIORITY_NONE: StatusMeta<'none'> = { value: 'none', label: 'No priority', tone: 'gray', order: 99 };

// ---------------------------------------------------------------------------
// KPI coverage judgment (see shared/kpiCoverage.ts) + row-level extras
// ---------------------------------------------------------------------------

export type KpiJudgmentKey = CoverageJudgment | 'no-data' | 'stale';

export const KPI_JUDGMENT: Record<KpiJudgmentKey, StatusMeta<KpiJudgmentKey>> = {
  achieved: { value: 'achieved', label: 'Achieved', tone: 'green', order: 0, hint: 'Completed count has reached the target' },
  healthy: { value: 'healthy', label: 'Healthy', tone: 'emerald', order: 1, hint: 'Pipeline is 150% of the target or more' },
  watch: { value: 'watch', label: 'Watch', tone: 'yellow', order: 2, hint: 'Pipeline covers the target but has little margin (100-149%)' },
  short: { value: 'short', label: 'Short', tone: 'red', order: 3, hint: 'Pipeline is below the target (under 100%) - add more activities' },
  manual: { value: 'manual', label: 'In progress', tone: 'blue', order: 4, hint: 'Manually recorded KPI, not yet at target' },
  stale: { value: 'stale', label: 'Stale', tone: 'amber', order: 5, hint: 'No record for the previous month' },
  'no-data': { value: 'no-data', label: 'No data', tone: 'gray', order: 6, hint: 'Nothing recorded yet' },
  'no-target': { value: 'no-target', label: 'No target', tone: 'gray', order: 7, hint: 'Set a target value to judge coverage' },
};

// ---------------------------------------------------------------------------
// Due state for tasks
// ---------------------------------------------------------------------------

export type DueState = 'overdue' | 'today' | 'week';

export const DUE_STATE: Record<DueState, StatusMeta<DueState>> = {
  overdue: { value: 'overdue', label: 'Overdue', tone: 'red', order: 0 },
  today: { value: 'today', label: 'Due today', tone: 'orange', order: 1 },
  week: { value: 'week', label: 'Due this week', tone: 'amber', order: 2 },
};

// ---------------------------------------------------------------------------
// Lookups with safe fallbacks (unknown values from old data render as gray + raw text)
// ---------------------------------------------------------------------------

function fallbackMeta(value: string): StatusMeta {
  return { value, label: value || '-', tone: 'gray', order: 999 };
}

export function taskStatusMeta(value: string | null | undefined): StatusMeta {
  return (value && (TASK_STATUS as Record<string, StatusMeta>)[value]) || fallbackMeta(value ?? '');
}

export function projectStatusMeta(value: string | null | undefined): StatusMeta {
  return (value && (PROJECT_STATUS as Record<string, StatusMeta>)[value]) || fallbackMeta(value ?? '');
}

export function priorityMeta(value: string | null | undefined): StatusMeta {
  if (!value || value === 'none') return PRIORITY_NONE;
  return (PRIORITY as Record<string, StatusMeta>)[value] || fallbackMeta(value);
}

export function kpiJudgmentMeta(value: string | null | undefined): StatusMeta {
  return (value && (KPI_JUDGMENT as Record<string, StatusMeta>)[value]) || fallbackMeta(value ?? '');
}

export function statusLabel(kind: 'task' | 'project', value: string | null | undefined): string {
  return kind === 'task' ? taskStatusMeta(value).label : projectStatusMeta(value).label;
}

export function toneHex(tone: Tone, dark = false): string {
  return dark ? TONES[tone].hexDark : TONES[tone].hex;
}

export const taskStatusHex = (value: string | null | undefined, dark = false) => toneHex(taskStatusMeta(value).tone, dark);
export const projectStatusHex = (value: string | null | undefined, dark = false) => toneHex(projectStatusMeta(value).tone, dark);
export const priorityHex = (value: string | null | undefined, dark = false) => toneHex(priorityMeta(value).tone, dark);
